import React, { useState } from 'react';
import {
  CalendarRange,
  Zap,
  CheckCircle2,
  AlertCircle,
  Clock,
  Layers,
  GraduationCap,
  Sparkles,
  Sliders,
  Check,
  Edit2,
  Save,
  X,
  HelpCircle,
  FileSpreadsheet,
  ArrowRight,
  Info,
} from 'lucide-react';
import {
  Siswa,
  Kelas,
  PosTagihan,
  TagihanSiswa,
  SppGradeRule,
  KategoriSiswa,
} from '../../types/index.js';
import { useDialog } from '../ui/DialogProvider.js';

interface RecurringSppBillingViewProps {
  siswaList: Siswa[];
  kelasList: Kelas[];
  posList: PosTagihan[];
  sppRules: SppGradeRule[];
  tagihanList: TagihanSiswa[];
  tahunAjaranAktif: string;
  onCreateRule?: (data: Partial<SppGradeRule>) => Promise<void>;
  onUpdateRule: (id: string, data: Partial<SppGradeRule>) => Promise<void>;
  onGenerateRecurringSpp: (payload: any) => Promise<any>;
  onNavigateToTagihan?: (siswaId?: string) => void;
}

export const RecurringSppBillingView: React.FC<RecurringSppBillingViewProps> = ({
  siswaList,
  kelasList,
  posList,
  sppRules,
  tagihanList,
  tahunAjaranAktif,
  onCreateRule,
  onUpdateRule,
  onGenerateRecurringSpp,
  onNavigateToTagihan,
}) => {
  const { showAlert, showConfirm } = useDialog();

  // Allow generating bills for any kind of Pos Tagihan
  const bulananPos = posList;

  // Generator Form State
  const [selectedTahunAjaran, setSelectedTahunAjaran] = useState(tahunAjaranAktif || '2026/2027');
  const [selectedPosIds, setSelectedPosIds] = useState<string[]>([]);
  const [targetFilter, setTargetFilter] = useState<'ALL' | 'TINGKAT' | 'KELAS' | 'KATEGORI'>('ALL');
  const [targetValue, setTargetValue] = useState<string>('1');
  const [startMonth, setStartMonth] = useState('Juli');
  const [endMonth, setEndMonth] = useState('Juni');
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastResult, setLastResult] = useState<any | null>(null);

  // Tab State: Generator vs Rule Config
  const [activeTab, setActiveTab] = useState<'GENERATOR' | 'RULES_CONFIG' | 'MONITORING'>('GENERATOR');

  // Rule Editing State
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [editNominal, setEditNominal] = useState<number>(0);
  const [editPotongan, setEditPotongan] = useState<number>(0);
  const [editKeterangan, setEditKeterangan] = useState<string>('');

  // Add New Rule State
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newRuleData, setNewRuleData] = useState<Partial<SppGradeRule>>({
    tingkat: 1,
    kategori: 'REGULER',
    nominalSpp: 0,
    potongan: 0,
    keterangan: '',
    isAktif: true
  });

  // Selected filter for rules table
  const [filterRuleTingkat, setFilterRuleTingkat] = useState<number | 'ALL'>('ALL');

  const months = [
    'Juli',
    'Agustus',
    'September',
    'Oktober',
    'November',
    'Desember',
    'Januari',
    'Februari',
    'Maret',
    'April',
    'Mei',
    'Juni',
  ];

  // Calculate target student count
  const filteredStudents = siswaList.filter((s) => {
    if (s.status !== 'Aktif') return false;
    if (targetFilter === 'ALL') return true;
    if (targetFilter === 'TINGKAT') {
      const k = kelasList.find((item) => item.id === s.kelasId || item.nama === s.kelasNama);
      return k && k.tingkat === Number(targetValue);
    }
    if (targetFilter === 'KELAS') {
      return s.kelasId === targetValue || s.kelasNama === targetValue;
    }
    if (targetFilter === 'KATEGORI') {
      return (s.kategori || 'REGULER') === targetValue;
    }
    return true;
  });

  // Calculate months count
  const startIdx = months.indexOf(startMonth);
  const endIdx = months.indexOf(endMonth);
  const monthRangeCount = Math.max(1, endIdx >= startIdx ? endIdx - startIdx + 1 : 12);
  const estimatedBillsToCreate = filteredStudents.length * monthRangeCount;

  // Handle Generate
  const handleExecuteGenerate = async () => {
    if (selectedPosIds.length === 0) {
      alert('Pilih minimal satu Pos Tagihan!');
      return;
    }

    const posNames = selectedPosIds.map(id => {
      const p = bulananPos.find(x => x.id === id);
      return p ? p.namaPos : id;
    }).join(', ');

    const confirmMsg =
      `Konfirmasi Pembuatan Tagihan Berkala Otomatis:\n\n` +
      `• Tahun Ajaran: ${selectedTahunAjaran}\n` +
      `• Pos Tagihan (${selectedPosIds.length}): ${posNames}\n` +
      `• Target: ${targetFilter} (${filteredStudents.length} Siswa Aktif)\n` +
      `• Periode: ${startMonth} s/d ${endMonth} (${monthRangeCount} Bulan)\n` +
      `• Estimasi Record: ±${estimatedBillsToCreate * selectedPosIds.length} Tagihan Bulanan\n` +
      `• Aturan Penyesuaian: Mengikuti Tingkat Kelas (1-6) & Kategori Siswa\n\n` +
      `Lanjutkan proses generate otomatis sekarang?`;

    const ok = await showConfirm(confirmMsg, { title: 'Generate Tagihan SPP Berkala', type: 'warning' });
    if (!ok) return;

    try {
      setIsGenerating(true);
      
      let totalCreated = 0;
      let totalSkipped = 0;
      let totalUpdated = 0;
      let anySuccess = false;

      for (const posId of selectedPosIds) {
        const res = await onGenerateRecurringSpp({
          tahunAjaran: selectedTahunAjaran,
          targetFilter,
          targetValue,
          startMonth,
          endMonth,
          overwriteExisting,
          posId: posId,
        });

        if (res && res.success) {
          anySuccess = true;
          totalCreated += res.data?.createdCount || 0;
          totalSkipped += res.data?.skippedCount || 0;
          totalUpdated += res.data?.updatedCount || 0;
        }
      }

      if (anySuccess) {
        setLastResult({
          message: 'Generate Tagihan Multi Pos Selesai!',
          summary: {
            totalStudentsProcessed: filteredStudents.length,
            startMonth,
            endMonth,
            monthsCount: monthRangeCount,
            generatedCount: totalCreated,
            skippedExistingCount: totalSkipped,
          }
        });
        showAlert(`Generate Berhasil!\n\nTagihan Baru Dibuat: ${totalCreated}\nDi-skip (Sudah ada): ${totalSkipped}\nDi-update: ${totalUpdated}`, { type: 'success', title: 'Sukses' });
      }
    } catch (err: any) {
      showAlert('Gagal membuat tagihan berkala: ' + (err?.message || 'Terjadi kesalahan sistem'), { type: 'danger', title: 'Gagal' });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleStartEditRule = (rule: SppGradeRule) => {
    setEditingRuleId(rule.id);
    setEditNominal(rule.nominalSpp);
    setEditPotongan(rule.potongan);
    setEditKeterangan(rule.keterangan || '');
  };

  const handleSaveRule = async (id: string) => {
    await onUpdateRule(id, {
      nominalSpp: editNominal,
      potongan: editPotongan,
      keterangan: editKeterangan,
    });
    setEditingRuleId(null);
  };

  const handleSaveNewRule = async () => {
    if (!onCreateRule) return;
    await onCreateRule(newRuleData);
    setIsAddingNew(false);
    setNewRuleData({
      tingkat: 1,
      kategori: 'REGULER',
      nominalSpp: 0,
      potongan: 0,
      keterangan: '',
      isAktif: true
    });
  };

  // Filtered Rules
  const visibleRules = sppRules.filter((r) => {
    if (filterRuleTingkat !== 'ALL' && r.tingkat !== filterRuleTingkat) return false;
    return true;
  });

  // Category badge helper
  const getCategoryBadge = (kat: KategoriSiswa) => {
    switch (kat) {
      case 'BEASISWA_PRESTASI':
        return (
          <span className="bg-amber-100 text-amber-900 border border-amber-300 font-semibold px-2 py-0.5 rounded text-[10px]">
            Beasiswa Prestasi
          </span>
        );
      case 'BEASISWA_YATIM':
        return (
          <span className="bg-emerald-100 text-emerald-900 border border-emerald-300 font-semibold px-2 py-0.5 rounded text-[10px]">
            Beasiswa Yatim
          </span>
        );
      case 'ANAK_GURU':
        return (
          <span className="bg-indigo-100 text-indigo-900 border border-indigo-300 font-semibold px-2 py-0.5 rounded text-[10px]">
            Anak Guru / Pegawai
          </span>
        );
      case 'AFIRMASI':
        return (
          <span className="bg-purple-100 text-purple-900 border border-purple-300 font-semibold px-2 py-0.5 rounded text-[10px]">
            Afirmasi Khusus
          </span>
        );
      default:
        return (
          <span className="bg-slate-100 text-slate-700 border border-slate-300 font-semibold px-2 py-0.5 rounded text-[10px]">
            Reguler
          </span>
        );
    }
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Top Header */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-bold text-slate-900">
              Modul Tagihan SPP Berkala Otomatis (Recurring Billing)
            </h2>
            <span className="bg-sky-100 text-sky-800 text-[10px] font-extrabold px-2 py-0.5 rounded-full border border-sky-300">
              Satu Tahun Ajaran Penuh
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Otomatisasi pembuatan tagihan SPP 12 bulan (Juli–Juni) berdasarkan tingkat kelas (Fase A–C) dan kategori siswa
            (Reguler, Prestasi, Yatim, Anak Guru, Afirmasi)
          </p>
        </div>

        {/* Tab navigation */}
        <div className="flex items-center bg-slate-100 p-1 rounded-lg border border-slate-200 text-xs">
          <button
            onClick={() => setActiveTab('GENERATOR')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition ${
              activeTab === 'GENERATOR'
                ? 'bg-white text-sky-800 shadow-xs font-bold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Zap className="w-3.5 h-3.5" />
            <span>Generate Massal</span>
          </button>
          <button
            onClick={() => setActiveTab('RULES_CONFIG')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition ${
              activeTab === 'RULES_CONFIG'
                ? 'bg-white text-sky-800 shadow-xs font-bold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Sliders className="w-3.5 h-3.5" />
            <span>Matriks Tarif SPP</span>
          </button>
          <button
            onClick={() => setActiveTab('MONITORING')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition ${
              activeTab === 'MONITORING'
                ? 'bg-white text-sky-800 shadow-xs font-bold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <CalendarRange className="w-3.5 h-3.5" />
            <span>Ringkasan & Validasi</span>
          </button>
        </div>
      </div>

      {/* Success Notification Alert if just generated */}
      {lastResult && (
        <div className="bg-emerald-50 border border-emerald-300 p-4 rounded-xl text-xs text-emerald-900 space-y-2 animate-in fade-in">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2 font-bold text-sm text-emerald-800">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>{lastResult.message}</span>
            </div>
            <button
              onClick={() => setLastResult(null)}
              className="text-emerald-700 hover:text-emerald-900 p-0.5 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
            <div className="bg-white/80 p-2.5 rounded-lg border border-emerald-200">
              <span className="text-[11px] text-emerald-700 font-medium">Siswa Diproses</span>
              <p className="text-base font-extrabold text-emerald-950 font-mono">
                {lastResult.summary?.totalStudentsProcessed} Siswa
              </p>
            </div>
            <div className="bg-white/80 p-2.5 rounded-lg border border-emerald-200">
              <span className="text-[11px] text-emerald-700 font-medium">Rentang Bulan</span>
              <p className="text-base font-extrabold text-emerald-950">
                {lastResult.summary?.startMonth} – {lastResult.summary?.endMonth} ({lastResult.summary?.monthsCount} Bln)
              </p>
            </div>
            <div className="bg-white/80 p-2.5 rounded-lg border border-emerald-200">
              <span className="text-[11px] text-emerald-700 font-medium">Record Dibuat</span>
              <p className="text-base font-extrabold text-emerald-950 font-mono">
                {lastResult.summary?.generatedCount} Tagihan
              </p>
            </div>
            <div className="bg-white/80 p-2.5 rounded-lg border border-emerald-200">
              <span className="text-[11px] text-emerald-700 font-medium">Dilewati (Sudah Ada)</span>
              <p className="text-base font-extrabold text-slate-700 font-mono">
                {lastResult.summary?.skippedExistingCount} Tagihan
              </p>
            </div>
          </div>
          <div className="pt-2 flex justify-end">
            <button
              onClick={() => onNavigateToTagihan && onNavigateToTagihan('ALL')}
              className="flex items-center gap-1 font-bold text-emerald-800 hover:text-emerald-950 underline cursor-pointer"
            >
              <span>Lihat Hasil Tagihan di Menu Tagihan Siswa</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* TAB 1: GENERATOR FORM */}
      {activeTab === 'GENERATOR' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left / Center 2 Cols: Main Configuration Form */}
          <div className="lg:col-span-2 bg-white p-5 rounded-xl border border-slate-200 shadow-xs space-y-5 text-xs">
            <div className="border-b border-slate-100 pb-3">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Zap className="w-4 h-4 text-sky-600" />
                <span>Parameter Pembuatan Tagihan Berkala Otomatis</span>
              </h3>
              <p className="text-slate-500 text-[11px] mt-0.5">
                Sistem akan membaca kelas dan kategori masing-masing siswa, kemudian mengalokasikan tarif SPP yang sesuai
                secara otomatis untuk setiap bulan dalam kalender akademik.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Tahun Ajaran */}
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Tahun Ajaran Target</label>
                <select
                  value={selectedTahunAjaran}
                  onChange={(e) => setSelectedTahunAjaran(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 font-medium focus:ring-1 focus:ring-sky-500"
                >
                  <option value="2026/2027">2026/2027 (Aktif)</option>
                  <option value="2027/2028">2027/2028 (Tahun Depan)</option>
                  <option value="2025/2026">2025/2026 (Tahun Lalu)</option>
                </select>
              </div>

              {/* Pos Tagihan */}
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Pos Tagihan (Bisa Pilih Banyak)</label>
                <div className="border border-slate-200 rounded-lg p-2 max-h-40 overflow-y-auto bg-slate-50 space-y-2">
                  {bulananPos.length === 0 ? (
                    <div className="text-[10px] text-slate-400 p-2">Tidak ada Pos Tagihan.</div>
                  ) : (
                    bulananPos.map(pos => (
                      <label key={pos.id} className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={selectedPosIds.includes(pos.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPosIds(prev => [...prev, pos.id]);
                            } else {
                              setSelectedPosIds(prev => prev.filter(id => id !== pos.id));
                            }
                          }}
                          className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                        />
                        {pos.kode} - {pos.namaPos}
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Filter Scope Target */}
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Cakupan Siswa (Target Sasaran)</label>
                <select
                  value={targetFilter}
                  onChange={(e) => {
                    setTargetFilter(e.target.value as any);
                    if (e.target.value === 'TINGKAT') setTargetValue('1');
                    else if (e.target.value === 'KELAS') setTargetValue(kelasList[0]?.id || 'k-1');
                    else if (e.target.value === 'KATEGORI') setTargetValue('REGULER');
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white font-medium focus:ring-1 focus:ring-sky-500"
                >
                  <option value="ALL">Semua Siswa Aktif Seluruh Sekolah</option>
                  <option value="TINGKAT">Berdasarkan Tingkat Kelas (1 s/d 6)</option>
                  <option value="KELAS">Berdasarkan Rombel Kelas Tertentu</option>
                  <option value="KATEGORI">Berdasarkan Kategori / Jalur Siswa</option>
                </select>
              </div>

              {/* Dynamic sub-target selector */}
              <div>
                <label className="block text-slate-700 font-semibold mb-1">
                  {targetFilter === 'TINGKAT'
                    ? 'Pilih Tingkat Kelas'
                    : targetFilter === 'KELAS'
                    ? 'Pilih Rombel Kelas'
                    : targetFilter === 'KATEGORI'
                    ? 'Pilih Kategori Siswa'
                    : 'Target Spesifik'}
                </label>
                {targetFilter === 'ALL' ? (
                  <input
                    type="text"
                    disabled
                    value={`Seluruh siswa aktif (${siswaList.filter((s) => s.status === 'Aktif').length} siswa)`}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-100 text-slate-500 italic"
                  />
                ) : targetFilter === 'TINGKAT' ? (
                  <select
                    value={targetValue}
                    onChange={(e) => setTargetValue(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white font-medium focus:ring-1 focus:ring-sky-500"
                  >
                    {[1, 2, 3, 4, 5, 6].map((lvl) => (
                      <option key={lvl} value={lvl}>
                        Tingkat {lvl} ({lvl <= 2 ? 'Fase A' : lvl <= 4 ? 'Fase B' : 'Fase C'})
                      </option>
                    ))}
                  </select>
                ) : targetFilter === 'KELAS' ? (
                  <select
                    value={targetValue}
                    onChange={(e) => setTargetValue(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white font-medium focus:ring-1 focus:ring-sky-500"
                  >
                    {kelasList.map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.nama} ({k.jumlahSiswa || 0} Siswa)
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={targetValue}
                    onChange={(e) => setTargetValue(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white font-medium focus:ring-1 focus:ring-sky-500"
                  >
                    <option value="REGULER">Reguler</option>
                    <option value="BEASISWA_PRESTASI">Beasiswa Prestasi</option>
                    <option value="BEASISWA_YATIM">Beasiswa Yatim</option>
                    <option value="ANAK_GURU">Anak Guru / Pegawai</option>
                    <option value="AFIRMASI">Afirmasi Khusus</option>
                  </select>
                )}
              </div>

              {/* Start Month */}
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Mulai Bulan (Awal T.A)</label>
                <select
                  value={startMonth}
                  onChange={(e) => setStartMonth(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white font-medium focus:ring-1 focus:ring-sky-500"
                >
                  {months.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              {/* End Month */}
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Sampai Bulan (Akhir T.A)</label>
                <select
                  value={endMonth}
                  onChange={(e) => setEndMonth(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white font-medium focus:ring-1 focus:ring-sky-500"
                >
                  {months.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Overwrite Option Checkbox */}
            <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-200">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={overwriteExisting}
                  onChange={(e) => setOverwriteExisting(e.target.checked)}
                  className="mt-0.5 rounded text-sky-600 focus:ring-sky-500"
                />
                <div>
                  <span className="font-semibold text-slate-800">
                    Sinkronkan & Timpa Tarif Tagihan Belum Lunas (Overwrite)
                  </span>
                  <p className="text-[11px] text-slate-500 leading-tight">
                    Jika dicentang, sistem akan memperbarui nominal tagihan SPP yang belum dibayar dengan tarif matriks terbaru.
                    Tagihan yang sudah dibayar (LUNAS / SEBAGIAN) tidak akan pernah diubah.
                  </p>
                </div>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="pt-2 flex items-center justify-between border-t border-slate-100">
              <div className="flex items-center gap-1.5 text-slate-600 text-xs">
                <Info className="w-4 h-4 text-sky-600" />
                <span>
                  Target terpilih: <strong>{filteredStudents.length} siswa</strong> × <strong>{monthRangeCount} bulan</strong>
                </span>
              </div>

              <button
                onClick={handleExecuteGenerate}
                disabled={isGenerating || filteredStudents.length === 0}
                className="flex items-center gap-2 bg-sky-700 hover:bg-sky-600 disabled:bg-slate-400 text-white font-bold px-5 py-2.5 rounded-lg transition shadow-sm cursor-pointer"
              >
                <Zap className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
                <span>{isGenerating ? 'Memproses Generate...' : 'Generate SPP Otomatis'}</span>
              </button>
            </div>
          </div>

          {/* Right 1 Col: Rule Matrix Preview & Student Breakdown */}
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs space-y-4 text-xs">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
              <h4 className="font-bold text-slate-900 flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-sky-600" />
                <span>Penerapan Tarif Otomatis</span>
              </h4>
              <button
                onClick={() => setActiveTab('RULES_CONFIG')}
                className="text-[11px] text-sky-700 font-semibold hover:underline"
              >
                Ubah Tarif
              </button>
            </div>

            <div className="space-y-2">
              <p className="text-[11px] text-slate-500">
                Tarif SPP akan dihitung secara dinamis dari kombinasi tingkat kelas dan kategori beasiswa masing-masing siswa:
              </p>

              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 divide-y divide-slate-200 space-y-2">
                {[1, 2, 3, 4, 5, 6].map((lvl) => {
                  const regRule = sppRules.find((r) => r.tingkat === lvl && r.kategori === 'REGULER');
                  const countInGrade = siswaList.filter((s) => {
                    const k = kelasList.find((kls) => kls.id === s.kelasId || kls.nama === s.kelasNama);
                    return k && k.tingkat === lvl;
                  }).length;

                  return (
                    <div key={lvl} className="pt-2 first:pt-0 flex items-center justify-between">
                      <div>
                        <span className="font-bold text-slate-800">Kelas {lvl}</span>
                        <span className="text-[10px] text-slate-500 ml-1.5">({countInGrade} siswa)</span>
                      </div>
                      <div className="text-right">
                        <span className="font-mono font-bold text-sky-900">
                          Rp {(regRule?.nominalSpp || 200000).toLocaleString('id-ID')}
                        </span>
                        <span className="text-[10px] text-slate-500 block">/ bulan (Reguler)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Category Subsidies info */}
            <div className="border-t border-slate-100 pt-3">
              <h5 className="font-bold text-slate-800 text-[11px] mb-1.5">Potongan Kategori Khusus (Berdasarkan Matriks Tarif):</h5>
              <ul className="space-y-1 text-[11px] text-slate-600">
                {['BEASISWA_PRESTASI', 'BEASISWA_YATIM', 'ANAK_GURU', 'AFIRMASI'].map((kat) => {
                  const rules = sppRules.filter((r) => r.kategori === kat);
                  let displayValue = 'Belum Diatur';
                  if (rules.length > 0) {
                    const potongans = rules.map((r) => r.potongan);
                    const min = Math.min(...potongans);
                    const max = Math.max(...potongans);
                    if (min === max) {
                      displayValue = min > 0 ? `-Rp ${min.toLocaleString('id-ID')} / bln` : 'Tidak Ada Potongan';
                    } else {
                      displayValue = `-Rp ${min.toLocaleString('id-ID')} - ${max.toLocaleString('id-ID')} / bln`;
                    }
                  }
                  
                  // Label mapping
                  const labelMap: Record<string, string> = {
                    'BEASISWA_PRESTASI': 'Beasiswa Prestasi',
                    'BEASISWA_YATIM': 'Beasiswa Yatim',
                    'ANAK_GURU': 'Anak Guru / Pegawai',
                    'AFIRMASI': 'Subsidi Afirmasi Khusus'
                  };

                  return (
                    <li key={kat} className="flex items-center justify-between">
                      <span>• {labelMap[kat]}</span>
                      <span className={`font-semibold ${displayValue === 'Belum Diatur' || displayValue === 'Tidak Ada Potongan' ? 'text-slate-500' : 'text-emerald-700'}`}>
                        {displayValue}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: RULES CONFIGURATION TABLE */}
      {activeTab === 'RULES_CONFIG' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
            <div>
              <h3 className="text-sm font-bold text-slate-900">
                Matriks Tarif SPP Berdasarkan Tingkat Kelas & Kategori Siswa
              </h3>
              <p className="text-xs text-slate-500">
                Tentukan standar nominal SPP dan potongan beasiswa untuk setiap jenjang kelas (Tingkat 1 s/d 6)
              </p>
            </div>

            <div className="flex items-center gap-3">
              {!isAddingNew && (
                <button
                  onClick={() => setIsAddingNew(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold rounded-md shadow-sm"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Tambah Aturan
                </button>
              )}
              <div className="flex items-center gap-2 border-l border-slate-200 pl-3">
                <span className="text-xs font-semibold text-slate-600">Filter Tingkat:</span>
              <select
                value={filterRuleTingkat}
                onChange={(e) =>
                  setFilterRuleTingkat(e.target.value === 'ALL' ? 'ALL' : Number(e.target.value))
                }
                className="text-xs border border-slate-300 rounded-md px-2.5 py-1.5 bg-slate-50 font-medium"
              >
                <option value="ALL">Semua Tingkat (1 - 6)</option>
                {[1, 2, 3, 4, 5, 6].map((l) => (
                  <option key={l} value={l}>
                    Tingkat {l}
                  </option>
                ))}
              </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider">
                <tr>
                  <th className="py-2.5 px-3 w-16 text-center">Tingkat</th>
                  <th className="py-2.5 px-4">Jenjang / Fase</th>
                  <th className="py-2.5 px-3">Kategori Siswa</th>
                  <th className="py-2.5 px-3 text-right">Tarif Dasar (Rp)</th>
                  <th className="py-2.5 px-3 text-right">Potongan / Subsidi (Rp)</th>
                  <th className="py-2.5 px-3 text-right">SPP Bersih Wajib (Rp)</th>
                  <th className="py-2.5 px-4">Keterangan</th>
                  <th className="py-2.5 px-3 text-center">Status</th>
                  <th className="py-2.5 px-3 text-center w-24">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isAddingNew && (
                  <tr className="bg-emerald-50/50 border-b-2 border-emerald-200">
                    <td className="py-2.5 px-3 text-center">
                      <select
                        value={newRuleData.tingkat}
                        onChange={(e) => setNewRuleData({ ...newRuleData, tingkat: Number(e.target.value) })}
                        className="w-full px-1.5 py-1 text-center border border-emerald-400 rounded bg-white text-xs"
                      >
                        {[1, 2, 3, 4, 5, 6].map(l => <option key={l} value={l}>{l}</option>)}
                      </select>
                    </td>
                    <td className="py-2.5 px-4 font-semibold text-emerald-900 text-xs">
                      Tingkat {newRuleData.tingkat}
                    </td>
                    <td className="py-2.5 px-3">
                      <select
                        value={newRuleData.kategori}
                        onChange={(e) => setNewRuleData({ ...newRuleData, kategori: e.target.value as KategoriSiswa })}
                        className="w-full px-1.5 py-1 border border-emerald-400 rounded bg-white text-xs font-semibold text-emerald-800"
                      >
                        <option value="REGULER">Reguler</option>
                        <option value="BEASISWA_PRESTASI">Beasiswa Prestasi</option>
                        <option value="BEASISWA_YATIM">Beasiswa Yatim</option>
                        <option value="ANAK_GURU">Anak Guru / Pegawai</option>
                        <option value="AFIRMASI">Afirmasi Khusus</option>
                      </select>
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <input
                        type="number"
                        value={newRuleData.nominalSpp}
                        onChange={(e) => setNewRuleData({ ...newRuleData, nominalSpp: Number(e.target.value) })}
                        className="w-24 px-1.5 py-1 text-right border border-emerald-400 rounded bg-white text-xs font-mono"
                      />
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <input
                        type="number"
                        value={newRuleData.potongan}
                        onChange={(e) => setNewRuleData({ ...newRuleData, potongan: Number(e.target.value) })}
                        className="w-24 px-1.5 py-1 text-right border border-emerald-400 rounded bg-white text-xs font-mono"
                      />
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono font-bold text-emerald-900 text-xs">
                      Rp {Math.max(0, (newRuleData.nominalSpp || 0) - (newRuleData.potongan || 0)).toLocaleString('id-ID')}
                    </td>
                    <td className="py-2.5 px-4">
                      <input
                        type="text"
                        placeholder="Keterangan..."
                        value={newRuleData.keterangan}
                        onChange={(e) => setNewRuleData({ ...newRuleData, keterangan: e.target.value })}
                        className="w-full px-2 py-1 border border-emerald-400 rounded bg-white text-xs"
                      />
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <span className="bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded text-[10px]">Aktif</span>
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={handleSaveNewRule}
                          className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-semibold text-[10px] flex items-center gap-0.5"
                        >
                          <Save className="w-3 h-3" />
                          <span>Simpan</span>
                        </button>
                        <button
                          onClick={() => setIsAddingNew(false)}
                          className="px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded font-semibold text-[10px]"
                        >
                          Batal
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                {visibleRules.map((rule) => {
                  const isEditing = editingRuleId === rule.id;
                  const net = Math.max(0, (isEditing ? editNominal : rule.nominalSpp) - (isEditing ? editPotongan : rule.potongan));

                  return (
                    <tr key={rule.id} className="hover:bg-sky-50/40 transition">
                      <td className="py-2.5 px-3 text-center font-bold font-mono text-slate-800">
                        {rule.tingkat}
                      </td>
                      <td className="py-2.5 px-4 font-semibold text-slate-900">
                        {rule.tingkatLabel || `Tingkat ${rule.tingkat}`}
                      </td>
                      <td className="py-2.5 px-3">{getCategoryBadge(rule.kategori)}</td>

                      {/* Nominal */}
                      <td className="py-2.5 px-3 text-right font-mono font-medium">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editNominal}
                            onChange={(e) => setEditNominal(Number(e.target.value))}
                            className="w-24 px-1.5 py-1 text-right border border-sky-500 rounded bg-white"
                          />
                        ) : (
                          rule.nominalSpp.toLocaleString('id-ID')
                        )}
                      </td>

                      {/* Potongan */}
                      <td className="py-2.5 px-3 text-right font-mono text-slate-600">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editPotongan}
                            onChange={(e) => setEditPotongan(Number(e.target.value))}
                            className="w-24 px-1.5 py-1 text-right border border-sky-500 rounded bg-white"
                          />
                        ) : (
                          rule.potongan.toLocaleString('id-ID')
                        )}
                      </td>

                      {/* Net */}
                      <td className="py-2.5 px-3 text-right font-mono font-bold text-sky-900">
                        Rp {net.toLocaleString('id-ID')}
                      </td>

                      {/* Keterangan */}
                      <td className="py-2.5 px-4 text-slate-600">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editKeterangan}
                            onChange={(e) => setEditKeterangan(e.target.value)}
                            className="w-full px-2 py-1 border border-sky-500 rounded bg-white"
                          />
                        ) : (
                          rule.keterangan || '-'
                        )}
                      </td>

                      <td className="py-2.5 px-3 text-center">
                        <span className="bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded text-[10px]">
                          Aktif
                        </span>
                      </td>

                      <td className="py-2.5 px-3 text-center">
                        {isEditing ? (
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleSaveRule(rule.id)}
                              className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-semibold text-[10px] flex items-center gap-0.5"
                            >
                              <Save className="w-3 h-3" />
                              <span>Simpan</span>
                            </button>
                            <button
                              onClick={() => setEditingRuleId(null)}
                              className="px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded font-semibold text-[10px]"
                            >
                              Batal
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleStartEditRule(rule)}
                            className="px-2 py-1 bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-300 rounded font-medium text-[10px] flex items-center justify-center gap-1 mx-auto"
                          >
                            <Edit2 className="w-3 h-3" />
                            <span>Ubah</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: MONITORING & AUDIT OF GENERATED BILLS */}
      {activeTab === 'MONITORING' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 space-y-4 text-xs">
          <div className="border-b border-slate-100 pb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold text-slate-900">
                Audit Kesiapan Tagihan SPP Berkala Siswa (T.A {tahunAjaranAktif})
              </h3>
              <p className="text-slate-500 text-[11px]">
                Statistik cakupan tagihan SPP per siswa untuk 12 bulan penuh tahun ajaran
              </p>
            </div>

            <button
              onClick={() => onNavigateToTagihan && onNavigateToTagihan('ALL')}
              className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold px-3 py-1.5 rounded-lg border border-slate-300"
            >
              <span>Buka Data Tagihan Lengkap</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-sky-50 p-4 rounded-xl border border-sky-200 space-y-1">
              <span className="text-sky-800 font-semibold text-[11px]">Total Siswa Aktif</span>
              <p className="text-xl font-extrabold text-sky-950 font-mono">
                {siswaList.filter((s) => s.status === 'Aktif').length} Siswa
              </p>
              <p className="text-[10px] text-sky-700">Terdaftar di 12 Rombongan Belajar</p>
            </div>

            <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200 space-y-1">
              <span className="text-emerald-800 font-semibold text-[11px]">Total Record SPP Tercatat</span>
              <p className="text-xl font-extrabold text-emerald-950 font-mono">
                {tagihanList.filter((t) => t.kodePos === 'SPP' || t.kodePos === '002' || t.posId === 'pos-2' || t.posId === 'pos-1').length} Tagihan
              </p>
              <p className="text-[10px] text-emerald-700">
                Lunas: {tagihanList.filter((t) => (t.kodePos === 'SPP' || t.kodePos === '002' || t.posId === 'pos-2' || t.posId === 'pos-1') && t.status === 'PAID').length} | Belum Lunas: {tagihanList.filter((t) => (t.kodePos === 'SPP' || t.kodePos === '002' || t.posId === 'pos-2' || t.posId === 'pos-1') && t.status === 'UNPAID').length}
              </p>
            </div>

            <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-200 space-y-1">
              <span className="text-indigo-800 font-semibold text-[11px]">Cakupan Kalender SPP</span>
              <p className="text-xl font-extrabold text-indigo-950">12 Bulan Penuh</p>
              <p className="text-[10px] text-indigo-700">Juli 2026 s/d Juni 2027</p>
            </div>
          </div>

          {/* Student-by-Student SPP Month Matrix */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-4 py-2.5 font-bold text-slate-800 border-b border-slate-200 flex items-center justify-between">
              <span>Status Tagihan SPP Bulanan Per Siswa</span>
              <span className="text-[10px] text-slate-500 font-normal">
                Indikator: Hijau = Lunas, Merah = Belum Lunas, Abu = Belum Di-generate
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-100 text-slate-600 font-semibold border-b">
                  <tr>
                    <th className="py-2 px-3">NIS</th>
                    <th className="py-2 px-3">Nama Siswa</th>
                    <th className="py-2 px-3">Kelas</th>
                    <th className="py-2 px-3">Kategori</th>
                    {months.map((m) => (
                      <th key={m} className="py-2 px-2 text-center text-[10px]">
                        {m.substring(0, 3)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {siswaList.slice(0, 10).map((s) => {
                    const studentSppBills = tagihanList.filter(
                      (t) => t.siswaId === s.id && (t.kodePos === 'SPP' || t.kodePos === '002' || t.posId === 'pos-2' || t.posId === 'pos-1')
                    );

                    return (
                      <tr key={s.id} className="hover:bg-slate-50">
                        <td className="py-2 px-3 font-mono font-medium">{s.nis}</td>
                        <td className="py-2 px-3 font-semibold text-slate-900">{s.nama}</td>
                        <td className="py-2 px-3">{s.kelasNama}</td>
                        <td className="py-2 px-3">{getCategoryBadge(s.kategori || 'REGULER')}</td>
                        {months.map((m) => {
                          const bill = studentSppBills.find((b) => b.bulan && b.bulan.startsWith(m));
                          return (
                            <td key={m} className="py-2 px-2 text-center">
                              {!bill ? (
                                <span className="inline-block w-2.5 h-2.5 rounded-full bg-slate-200" title={`${m}: Belum dibuat`} />
                              ) : bill.status === 'PAID' ? (
                                <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" title={`${m}: Lunas`} />
                              ) : (
                                <span className="inline-block w-2.5 h-2.5 rounded-full bg-rose-500" title={`${m}: Belum Lunas (Rp ${bill.sisa.toLocaleString('id-ID')})`} />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
